/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2022] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Backbone from 'backbone';
import Radio from 'backbone.radio';

import HeaderBarView from '@/views/HeaderBarView';
import DropdownCell from '@/backgrid/DropdownCell';
import ButtonCell from '@/backgrid/ButtonCell';
import showModal from '@/util/showModal';
import ConfirmationModalView from '@/views/ConfirmationModalView';

const LegacyAWPackageHeaderView = HeaderBarView.extend({
  className: 'aw-package-header-view header-bar-view clearfix',

  initialize() {
    const self = this;

    const columns = [
      {
        name: 'id',
        label: 'AW Package ID'
      },
      {
        name: 'awPackageStatus',
        label: 'Status'
      },
      {
        name: 'epcName',
        label: 'Assigned EPC'
      },
      {
        name: 'awFDRNum',
        label: 'Document #'
      },
      {
        name: 'formName',
        label: 'Type'
      },
      {
        renderable: !EXTERNAL,
        label: '',
        name: 'actions',
        headerCell: DropdownCell.extend({
          name: 'Actions',
          buttonClass: 'btn-outline-primary action-select mt-3 ms-auto me-3 col-3',
          buttonParentClass: `d-flex flex-row flex-grow-1 align-self-center`,
          optionFunction(data) {
            const options = [];
            if (self.options.showViewEditBtn) {
              if (self.options.isEditing) {
                options.push({ id: 'viewEditAWPackage', display: 'View AW Package' });
              } else {
                options.push({ id: 'viewEditAWPackage', display: 'Edit AW Package' });
              }
            }
            self.options.showReassignEPCBtn && options.push({ id: 'reassign', display: 'Reassign EPC' });
            self.options.showDeleteButton && options.push({ id: 'delete', display: 'Delete Draft' });
            self.options.showHideButton && options.push({ id: 'hide', display: 'Hide' });
            self.options.showPrintButton && options.push({ id: 'print', display: 'Print' });

            this.currentActions = options;

            return options;
          },
          async onClick(e, model) {
            const action = e.target.id;
            switch (action) {
              case 'viewEditAWPackage': {
                const isEditing = self.options.isEditing;
                self.toggleIsEditing(e);
                this.render();
                return isEditing ? self.onClickView(e, model) : self.onClickEdit(e, false);
              }
              case 'reassign':
                self.onClickReassignEPC(e); //
                break;
              case 'print':
                self.onClickPrint(e); //?
                break;
              case 'delete':
                self.onClickDeleteDraft(e);
                break;
              case 'hide':
                self.onClickHide(e);
                break;
            }
          }
        })
      }
    ].filter((col) => col.renderable !== false);

    this.options.columns = new Backbone.Collection(columns);

    this.listenTo(Radio.channel('aw-package'), 'update:header', (key, value) => {
      this.model.set(key, value);
    });
  },

  initEditAWPackageButtonCell() {
    const self = this;
    return ButtonCell.extend({
      name: self.options.isEditing ? 'View AW Package' : 'Edit AW Package',
      getName() {
        return self.options.isEditing ? 'View AW Package' : 'Edit AW Package';
      },
      setName() {
        this.$el.find('span')?.text(this.getName());
      },
      onClick(e, model) {
        const isEditing = self.options.isEditing;
        self.toggleIsEditing();
        this.setName();
        return isEditing ? self.onClickView(e, model) : self.onClickEdit(e, false);
      },
      buttonClass: 'btn btn-primary btn-md mt-3 me-3 ms-auto edit-view-order',
      buttonParentClass: 'd-flex flex-row flex-grow-1 flex-nowrap align-self-center'
    });
  },

  toggleIsEditing() {
    this.options.isEditing = !this.options.isEditing;
  },

  onClickEdit(e) {
    e.preventDefault();
    this.triggerMethod('reload', true);
  },

  onClickView(e, model) {
    e.preventDefault();
    this.triggerMethod('reload', false);
  },

  async onClickPrint(e, model) {
    e.preventDefault();
    Radio.channel('navigate').request('show:aw-package:print', this.model.get('id'));
  },

  async onClickReassignEPC(e, model) {
    e.preventDefault();
    this.triggerMethod('reassign:epc', model);
  },

  async onClickDeleteDraft(e, model) {
    e.preventDefault();
    const self = this;
    const message = `Are you sure you want to delete this AW Package?`;

    showModal({
      title: 'Delete AW Package',
      view: new ConfirmationModalView({
        confirmText: 'Delete',
        model: new Backbone.Model({
          message,
          onConfirm() {
            self.triggerMethod('delete:draft', model);
            return Promise.resolve();
          }
        })
      })
    });
  },

  async onClickHide(e, model) {
    e.preventDefault();
    const self = this;
    const message = `Are you sure you want to hide this AW Package?`;

    showModal({
      title: 'Hide AW Package',
      view: new ConfirmationModalView({
        confirmText: 'Hide',
        model: new Backbone.Model({
          message,
          onConfirm() {
            self.triggerMethod('hide', model);
            return Promise.resolve();
          }
        })
      })
    });
  },

  setAssignedEPC(name) {
    this.model.set('epcName', name);
  }
});

export default LegacyAWPackageHeaderView;
